import axios from "axios";
import * as FileSaver from "file-saver";
import { Supplier } from "../models/Supplier";

/**
 * RAG-29 — Écran Fournisseurs : choisir le PIM source de chaque fournisseur.
 * S'appuie sur l'API RAG-19 :
 *   - GET  admins/supplier-pim-sources            -> liste + sources autorisées
 *   - PUT  admins/supplier-pim-sources/{provider} -> fixe la règle + déclenche la resynchro
 * Le jeton admin est injecté automatiquement par l'intercepteur axios.
 */

export interface SuppliersList {
  suppliers: Supplier[];
  sources: string[];
}

function messageForStatus(error: any): string {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.error ?? error?.response?.data?.message;
  switch (status) {
    case 403:
      return "Vous n'avez pas les droits pour modifier la source d'un fournisseur.";
    case 422:
      return apiMessage || "Source invalide.";
    default:
      return "Veuillez contacter un administrateur";
  }
}

function list(): Promise<SuppliersList> {
  return axios
    .get(`${process.env.REACT_APP_API_URL}admins/supplier-pim-sources`)
    .then((response) => ({
      suppliers: (response.data?.data ?? []).map((row: any) => new Supplier(row)),
      sources: response.data?.sources ?? [],
    }));
}

/** Fixe la source d'un fournisseur ; résout avec le nombre de produits remis en file de resynchro. */
function updateSource(provider: string, source: string): Promise<number> {
  return new Promise((resolve, reject) => {
    axios
      .put(
        `${process.env.REACT_APP_API_URL}admins/supplier-pim-sources/${encodeURIComponent(provider)}`,
        { source }
      )
      .then((response) => resolve(response.data?.resynced_products ?? 0))
      .catch((error) => reject({ status: error?.response?.status, message: messageForStatus(error) }));
  });
}

/**
 * RAG-12 — télécharge le CSV des produits du fournisseur avec le statut de leur
 * visuel (OK / Manquant / Défectueux). L'appel est authentifié par l'intercepteur
 * axios ; la réponse est un flux CSV qu'on enregistre côté navigateur.
 */
function exportVisuals(provider: string): Promise<void> {
  return axios
    .get(`${process.env.REACT_APP_API_URL}admins/products/visuals-export`, {
      params: { provider },
      responseType: "blob",
    })
    .then((response) => {
      FileSaver.saveAs(new Blob([response.data]), `produits-visuels-${provider}.csv`);
    });
}

export default {
  list,
  updateSource,
  exportVisuals,
};
